import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { isAxiosError } from 'axios'
import { nanoid } from 'nanoid'
import { TableStore } from 'shared/ui/TableNew'
import { logger } from 'shared/lib'
import { showToast } from 'shared/ui'
import { RequestLoadData } from 'shared/api/lib'
import {
  CustomField,
  CustomFieldApi,
  CustomFieldRow,
  type IField,
  type IParamsPaginatedCustomFields,
} from 'entities/CustomField'
import { tableLimitStore } from 'entities/TableLimit'

export class CustomFieldsStore {
  readonly tableStore: TableStore<CustomFieldRow>
  private _initialLoading = true
  private _disposed = false
  private _hasRetriedAtMinLimit = false

  private _fieldsMap = new Map<string, CustomFieldRow>()
  private _fieldTypesMap = new Map<string, string>()
  private _search = ''
  private _scrollToTOpTrigger = ''
  private _page = 1
  private _limit = tableLimitStore.getLimit('settings-custom-fields-table') ?? 10

  private _disposeParamsWithoutPage: IReactionDisposer | null = null
  private _disposeParamsGetItems: IReactionDisposer | null = null

  constructor() {
    this.tableStore = new TableStore({
      key: 'settings_custom_fields',
      element: 'number',
      page: this._page,
      getLimit: () => tableLimitStore.getLimit('settings-custom-fields-table'),
      setLimit: (value) => tableLimitStore.setLimit('settings-custom-fields-table', value),
      limit: 10,
    })
    makeAutoObservable(this)
  }

  init = async () => {
    await Promise.all([this.loadCustomFields(), this.loadFieldTypes()])

    if (this._disposed) return

    runInAction(() => {
      this._initialLoading = false
    })

    this.reactionParamsWithoutPage()
    this.reactionParamsGetItems()
  }

  dispose = () => {
    this._disposed = true
    this._disposeParamsWithoutPage?.()
    this._disposeParamsGetItems?.()
    this._requestLoadCustomFields.dispose()
  }

  reactionParamsWithoutPage = () => {
    this._disposeParamsWithoutPage?.()
    this._disposeParamsWithoutPage = reaction(
      () => this._paramsWithoutPage,
      () => {
        this.tableStore.setLoading(true)
        this.tableStore.setPage(1)
        this._page = 1
      }
    )
  }

  reactionParamsGetItems = () => {
    this._disposeParamsGetItems?.()
    this._disposeParamsGetItems = reaction(
      () => this._listRequestParams,
      () => this._requestLoadCustomFields.debounceLoadData()
    )
  }

  private _requestLoadCustomFields = new RequestLoadData({
    requestCallback: async ({ cancelRequest }) => {
      runInAction(() => {
        this.tableStore.setLoading(true)
      })

      try {
        const { data } = await CustomFieldApi.getCustomFields(this._listRequestParams, {
          cancelToken: cancelRequest.token,
        })

        if (this._disposed) return

        if ('data' in data) {
          this.setData(data.data, data.total, data.current_page)
        }

        runInAction(() => {
          this._hasRetriedAtMinLimit = false
          this.tableStore.setLoading(false)
        })
      } catch (error) {
        const isTimeout =
          isAxiosError(error) && (error.response?.status === 408 || error.response?.status === 504)
        if (isTimeout) {
          logger.warn(
            'TABLE: settings-custom-fields-table Request timed out, likely due to large data volume. Consider adding more filters to reduce the dataset size.'
          )
          const currentLimit = this.tableStore.limit
          const newLimit = tableLimitStore.reduceLimit('settings-custom-fields-table', currentLimit)
          if (newLimit !== currentLimit) {
            runInAction(() => {
              this._hasRetriedAtMinLimit = false
              this._limit = newLimit
            })
            this.tableStore.setLimit(newLimit)
          } else if (!this._hasRetriedAtMinLimit) {
            runInAction(() => {
              this._hasRetriedAtMinLimit = true
            })
            this._requestLoadCustomFields.debounceLoadData()
          }
        } else {
          throw error
        }
      }
    },
    errorCallback: () => {
      showToast({
        type: 'error',
        title: 'Something went wrong. Please try again.',
      })
    },
    finallyCallback: () => {
      runInAction(() => {
        this.tableStore.setLoading(false)
      })
    },
  })

  loadCustomFields = async () => {
    return this._requestLoadCustomFields.loadData()
  }

  loadFieldTypes = async () => {
    try {
      const { data } = await CustomFieldApi.getCustomFieldTypes()

      if (this._disposed) return

      runInAction(() => {
        Object.entries(data.data.types).forEach(([key, label]) => {
          this._fieldTypesMap.set(key, label)
        })
      })
    } catch (error) {
      logger.error(error)
    }
  }

  setData = (data: IField[], total: number, currentPage: number) => {
    this._fieldsMap.clear()

    const rows = data.map((item) => new CustomFieldRow(new CustomField(item)))

    rows.forEach((row) => {
      this._fieldsMap.set(row.fieldKey, row)
    })

    this.tableStore.setTotal(total)
    this.tableStore.setPage(currentPage)
    this._page = currentPage
    this._scrollToTOpTrigger = nanoid()
  }

  changePaging = (page: number, limit: number) => {
    this._page = page
    this._limit = limit
  }

  handleDeleteSuccess = async () => {
    if (this._fieldsMap.size === 1 && this._page > 1) {
      this.tableStore.setLoading(true)
      this._page = this._page - 1
      this.tableStore.setPage(this._page)
    } else {
      await this.loadCustomFields()
    }
  }

  setSearch = (value: string) => {
    this._search = value
  }

  getFieldTypeLabel = (type: string): string => {
    return this._fieldTypesMap.get(type) ?? type
  }

  private get _paramsWithoutPage(): Omit<IParamsPaginatedCustomFields, 'page'> {
    const params: Omit<IParamsPaginatedCustomFields, 'page'> = {
      length: this._limit,
    }

    if (this._search) params.search = this._search

    return params
  }

  private get _listRequestParams(): IParamsPaginatedCustomFields {
    return {
      page: this._page,
      ...this._paramsWithoutPage,
    }
  }

  get initialLoading() {
    return this._initialLoading
  }

  get search() {
    return this._search
  }

  get scrollToTOpTrigger() {
    return this._scrollToTOpTrigger
  }

  get customFields() {
    return Array.from(this._fieldsMap.values())
  }
}
